import { Entity, mapEntity } from '@api';
import { formatDateToUtcIsoString, formatPercentage } from '@helpers';
import { useOneDayReturnLivePrice } from '@terminal/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { useFetchSnakeByName } from '@terminal/react-query-hooks/snakes/useFetchSnakeByName';
import {
  getOneDayReactionDate,
  getOneDayReactionValue,
} from '@terminal/widgets/earnings/utils/utils';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './company-card/CompanyCard.module.scss';
import { formatDateToET } from './utils';

const ONE_DAY_RETURN_SNAKE = 'mom(horiz=1d)';

interface EarningsPillProps {
  isReported: boolean;
  entity: Entity;
  reportingDate: string;
}

export const EarningsPill = ({
  isReported,
  entity,
  reportingDate,
}: EarningsPillProps) => {
  const { t } = useTranslation(['earnings']);
  const mappedEntity = useMemo(() => mapEntity(entity), [entity]);
  const { oneDayLiveReturn } = useOneDayReturnLivePrice(mappedEntity);

  const dateToUse = reportingDate
    ? getOneDayReactionDate(new Date(reportingDate))
    : undefined;

  const earningsDateFormatted = reportingDate
    ? formatDateToET(new Date(reportingDate))
    : undefined;

  const formattedFromDate = dateToUse
    ? formatDateToUtcIsoString(dateToUse)
    : undefined;

  const { data: activeSnakeData } = useFetchSnakeByName(
    `${entity.tag}.${ONE_DAY_RETURN_SNAKE}`,
    undefined,
    formattedFromDate,
    !formattedFromDate
  );

  const oneDayReaction = getOneDayReactionValue(
    dateToUse,
    oneDayLiveReturn,
    activeSnakeData,
    formattedFromDate
  );

  const dateTitleLabel = isReported
    ? t('earnings:earningsInsight.drawer.earningsPreview.reported')
    : t('earnings:earningsInsight.drawer.earningsPreview.scheduled');

  return (
    <>
      {earningsDateFormatted && (
        <div
          className={clsx(
            styles.earningsSection,
            isReported && styles.earningsSectionReported
          )}
        >
          <div className={styles.earningsDateWrapper}>
            <span className={styles.earningsDateTitleLabel}>
              {dateTitleLabel}
            </span>
            <span className={styles.earningsDateValueLabel}>
              {earningsDateFormatted}
            </span>
          </div>
          {isReported && oneDayReaction !== undefined && (
            <div className={styles.earningsReactionWrapper}>
              <span className={styles.earningsReactionTitleLabel}>
                {t('earnings:earningsInsight.drawer.earningsPreview.reaction')}
              </span>
              <span
                className={clsx(
                  styles.earningsReactionValueLabel,
                  oneDayReaction > 0 &&
                    styles.earningsReactionValueLabelPositive
                )}
              >
                {formatPercentage(oneDayReaction, {
                  suffix: '%',
                  addPlus: true,
                  useOriginalValue: true,
                })}
              </span>
            </div>
          )}
        </div>
      )}
    </>
  );
};
